import React, {useEffect, useState} from 'react';
import {Label} from '../../models/Label';
import {t} from 'i18next';
import {ContentWrapper, LabelGridWrapper, TextInputLong, TextInputShort} from './LabelTab.style';
import {getEmptyLabel} from '../../utils/StorageObjectUtils';
import {isValidText} from '../../utils';
import {Valid} from '../validation/Valid';
import {generateIdentifier} from '../../utils/IdentityUtils';
import {isMandatoryComponent, isVisibleComponent} from '../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_VERSION
} from '../../init/storage_object_constants';
import {TemplatePropertySettings} from '../../models/TemplatePropertySettings';

export interface LabelTabProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly locale: string;
    readonly label: Label;
    readonly version: string;
    onChangeLabel: (label: Label) => void;
    onChangeVersion: (version: string) => void;
}

/**
 * @name LabelTab
 * @description
 * Component per StorageObject Label Tab
 */
export const LabelTab: React.VFC<LabelTabProps> = (props) => {
    const [labelInput, setLabelInput] = useState<Label>(props.label ?? getEmptyLabel());
    const [nameInput, setNameInput] = useState(props.label.name ?? '');
    const [descriptionInput, setDescriptionInput] = useState(props.label.description ?? '');
    const [versionInput, setVersionInput] = useState(props.version ?? '');

    useEffect(() => {
        setLabelInput(props.label ?? getEmptyLabel());
        setNameInput(props.label.name ?? '');
        setDescriptionInput(props.label.description ?? '');
    }, [props.label]);

    useEffect(() => {
        setVersionInput(props.version ?? '');
    }, [props.version]);

    const handleVersionInput = (e: { target: { value: React.SetStateAction<string> } }) => {
        let eventValue = e.target.value.toString();
        props.onChangeVersion(eventValue)
        setVersionInput(eventValue);
    };

    const handleNameInput = (e: { target: { value: React.SetStateAction<string> } }) => {
        let eventValue = e.target.value.toString();
        let label = labelInput;
        label.name = eventValue;
        props.onChangeLabel(label);
        setLabelInput(label);
        setNameInput(eventValue);
    };

    const handleDescriptionInput = (e: { target: { value: React.SetStateAction<string> } }) => {
        let eventValue = e.target.value.toString();
        let label = labelInput;
        label.description = eventValue;
        props.onChangeLabel(label);
        setLabelInput(label);
        setDescriptionInput(eventValue);
    };

    const isValidLabelGrid = (): boolean => {
        return ((isMandatoryComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, props.templatePropertySettings) ? isValidText(nameInput) : true)
            && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, props.templatePropertySettings) ? isValidText(versionInput) : true)) as boolean;
    };

    const getLabelGridValidationMessage = (): string => {
        if (!(isMandatoryComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, props.templatePropertySettings) ? isValidText(nameInput) : true)) {
            return t('META_DATA_DIALOG_NAME_NOT_VALID_CONSTRAINT');
        }

        if (!(isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, props.templatePropertySettings) ? isValidText(versionInput) : true)) {
            return t('META_DATA_DIALOG_VERSION_NOT_VALID_CONSTRAINT');
        }

        return '';
    };

    return (
        <ContentWrapper id={generateIdentifier()}>
            {((isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, props.templatePropertySettings))
                    || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, props.templatePropertySettings))) &&
                <Valid isValid={isValidLabelGrid()}
                       message={getLabelGridValidationMessage()}>
                    <LabelGridWrapper id={generateIdentifier()}>
                        {(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)
                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, props.templatePropertySettings)) &&
                            <TextInputShort id={generateIdentifier('name')}
                                            label={`${t('META_DATA_DIALOG_NAME_LABEL')}*`}
                                            required
                                            value={nameInput}
                                            onChange={handleNameInput}/>
                        }
                        <div id={generateIdentifier()}/>
                        {isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, props.templatePropertySettings) &&
                            <TextInputShort id={generateIdentifier('version')}
                                            label={`${t('META_DATA_DIALOG_VERSION_LABEL')}*`}
                                            required
                                            value={versionInput}
                                            onChange={handleVersionInput}/>
                        }
                    </LabelGridWrapper>
                </Valid>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, props.templatePropertySettings)) &&
                <Valid
                    isValid={(isMandatoryComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, props.templatePropertySettings) ? isValidText(descriptionInput) : true)}
                    message={t('META_DATA_DIALOG_DESCRIPTION_NOT_VALID_CONSTRAINT')}>
                    <TextInputLong id={generateIdentifier('description')}
                                   label={`${t('META_DATA_DIALOG_DESCRIPTION_LABEL')}*`}
                                   required
                                   value={descriptionInput}
                                   onChange={handleDescriptionInput}/>
                </Valid>
            }
        </ContentWrapper>
    );
};
